import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Slide, Stack, Typography, Box, InputLabel, Select, MenuItem, IconButton, FormHelperText } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as GlobalIcon } from "../../../../../../../vendor/_new/icon/services/global.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../../../vendor/_new/icon/sites/arrow_down.svg";

import colors from "../../../../../../../utils/colors";
import { GreenSelectForm, GreenSwitch } from "../../../../../../common/Greens";
import { SaveButton, CancelButton } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import { WafAction, SmartParseSensitivity, UserType, WafType, ChallengeKind, isOrgUserType } from "../../../../../../../utils/constants";
import useAuth from "../../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import usePermissions from "../../../../../../../hooks/usePermissions";
import useSite from "../../../../../../../hooks/user/useSite";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateWafSettingsForm } from "../../../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";

const helperTextSx = { fontSize: 12, color: colors.neutral[600], mx: 0, mt: 0.5 };

export const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const ConfigSmartParseWAFSettingsModal = ({ siteUid, open, handleClose }) => {
  const { userType } = useAuth();
  const { wafConfig, configWafSetting, getWAFConfig } = useWAFConfig();
  const { getPermissionsForSite, PERMISSIONS } = usePermissions();
  const { site_List } = useSite();
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = React.useState(false);
  const [smart_parse_module_active, set_smart_parse_module_active] = React.useState(false);
  const [smart_parse_sensitivity, set_smart_parse_sensitivity] = React.useState(SmartParseSensitivity.MEDIUM);
  const [waf_action_smart_parse, set_waf_action_smart_parse] = React.useState(WafAction.DETECT);
  // wafChallengeKindSmartParse: nullable challenge variant; only meaningful
  // when the SmartParse action is Challenge. NULL in UI state means "let
  // backend default (managed)"; PATCH key is `waf_challenge_kind_smart_parse`.
  const [waf_challenge_kind_smart_parse, set_waf_challenge_kind_smart_parse] = React.useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  // Org admins and platform admins configure WAF org-wide; org users need project/site RBAC (same gate as handleSave)
  const canConfigureWAF = useMemo(() => {
    if (!isOrgUserType(userType)) {
      return true;
    }
    if (!siteUid || !site_List?.length) return false;
    const site = site_List.find((s) => s.id === siteUid);
    if (!site) return false;
    return getPermissionsForSite(site).hasPermission(PERMISSIONS.WAF_CONFIGURE);
  }, [userType, siteUid, site_List, getPermissionsForSite, PERMISSIONS]);

  const formValidation = useMemo(
    () =>
      validateWafSettingsForm({
        variant: "smartparse",
        smart_parse_sensitivity,
        waf_action_smart_parse,
        waf_challenge_kind_smart_parse,
      }),
    [smart_parse_sensitivity, waf_action_smart_parse, waf_challenge_kind_smart_parse],
  );

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors],
  );

  const canSubmit = canConfigureWAF;

  useEffect(() => {
    if (!open) {
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [open]);

  useEffect(() => {
    if (wafConfig) {
      set_smart_parse_module_active(!!wafConfig?.smart_parse_module_active);
      set_smart_parse_sensitivity(wafConfig?.smart_parse_sensitivity || SmartParseSensitivity.MEDIUM);
      set_waf_action_smart_parse(wafConfig?.waf_action_smart_parse || WafAction.DETECT);
      set_waf_challenge_kind_smart_parse(wafConfig?.waf_challenge_kind_smart_parse ?? null);
      if (open) setBaselineKey((k) => k + 1);
    }
  }, [wafConfig, open]);

  const formSnapshot = useMemo(
    () => ({
      smart_parse_module_active,
      smart_parse_sensitivity,
      waf_action_smart_parse,
      waf_challenge_kind_smart_parse,
    }),
    [smart_parse_module_active, smart_parse_sensitivity, waf_action_smart_parse, waf_challenge_kind_smart_parse],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, { disabled: saving });

  const handleSave = async () => {
    setSubmitAttempted(true);
    const validation = validateWafSettingsForm({
      variant: "smartparse",
      smart_parse_sensitivity,
      waf_action_smart_parse,
      waf_challenge_kind_smart_parse,
    });
    if (!validation.valid) return;

    // Check permissions before saving
    if (!canConfigureWAF) {
      showError("You don't have permission to configure WAF for this site");
      return;
    }
    
    setSaving(true);
    if (userType !== UserType.ORG_USER) {
      try {
        const tasks = [];
        if (wafConfig?.smart_parse_module_active !== smart_parse_module_active) {
          tasks.push(() => configWafSetting(siteUid, "smartParseWafActiveChange", { enable: smart_parse_module_active }));
        }
        if (wafConfig?.smart_parse_sensitivity !== smart_parse_sensitivity) {
          tasks.push(() => configWafSetting(siteUid, "smartParseSensitivityChange", { sensitivity: smart_parse_sensitivity }));
        }
        if (wafConfig?.waf_action_smart_parse !== waf_action_smart_parse) {
          tasks.push(() => configWafSetting(siteUid, "smartParseWafActionChange", { cate_id: WafType.SMART_PARSE, action: waf_action_smart_parse }));
        }
        // Only relevant when action is Challenge; the picker is hidden otherwise
        // so we won't accidentally PATCH a stale kind for Detect/Block.
        if (waf_action_smart_parse === WafAction.CHALLENGE &&
            (wafConfig?.waf_challenge_kind_smart_parse ?? null) !== waf_challenge_kind_smart_parse) {
          tasks.push(() =>
            configWafSetting(siteUid, "smartParseChallengeKindChange", { challenge_kind: waf_challenge_kind_smart_parse })
          );
        }
        if (tasks.length > 0) {
          for (const task of tasks) {
            await task();
          }
          await getWAFConfig(siteUid);
          showSuccess("Smart Parse WAF settings saved successfully");
        } else {
          showSuccess("No changes to save");
        }
      } catch (err) {
        console.error(err);
        // Show the specific control-plane reason (e.g. plan limit / validation)
        // surfaced by configWafSetting, not just a generic failure.
        showError(err?.message || "Failed to save Smart Parse WAF settings");
      }
    }
    setSaving(false);
    handleClose();
  };

  return (
    <>
    <Modal 
      open={open} 
      onClose={requestClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GlobalIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    Layerd AI Smart Parse settings
                  </Typography>
                  <FormHelpTooltip tooltip="Configure the Smart Parse ML engine: enable/disable, sensitivity, default action, and challenge type for AI-driven threat detection on request payloads." />
                </Stack>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Configure advanced AI reasoning threat detection.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>
          <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={3}
                    justifyContent={"space-between"}
                    sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[300]}`, backgroundColor: "white", p: 3 }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      Layerd AI Smart Parse
                    </Typography>
                    <GreenSwitch
                      disabled={saving || !canConfigureWAF}
                      checked={smart_parse_module_active}
                      onChange={(e) => set_smart_parse_module_active(e.target.checked)}
                    />
                  </Stack>
                  <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("smart_parse_sensitivity"))}>
                    <InputLabel id="smartparse_sensitivity">Smart Parse Sensitivity</InputLabel>
                    <Select
                      labelId="smartparse_sensitivity"
                      IconComponent={ArrowdownIcon}
                      disabled={saving || !canConfigureWAF}
                      value={smart_parse_sensitivity}
                      onClose={() => touchField("smart_parse_sensitivity")}
                      onChange={(e) => {
                        set_smart_parse_sensitivity(parseInt(e.target.value));
                      }}
                    >
                      <MenuItem value={SmartParseSensitivity.HIGH} key={`SmartParseSensitivity${SmartParseSensitivity.HIGH}`} sx={menuItemSx}>
                        High
                      </MenuItem>
                      <MenuItem value={SmartParseSensitivity.MEDIUM} key={`SmartParseSensitivity${SmartParseSensitivity.MEDIUM}`} sx={menuItemSx}>
                        Medium
                      </MenuItem>
                      <MenuItem value={SmartParseSensitivity.LOW} key={`SmartParseSensitivity${SmartParseSensitivity.LOW}`} sx={menuItemSx}>
                        Low
                      </MenuItem>
                    </Select>
                    {showFieldErr("smart_parse_sensitivity") && (
                      <FormHelperText error sx={helperTextSx}>{formValidation.errors.smart_parse_sensitivity}</FormHelperText>
                    )}
                  </GreenSelectForm>
                  <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("waf_action_smart_parse"))}>
                    <InputLabel id="waf_action_smartparse">WAF Action</InputLabel>
                    <Select
                      labelId="waf_action_smartparse"
                      IconComponent={ArrowdownIcon}
                      disabled={saving || !canConfigureWAF}
                      value={waf_action_smart_parse}
                      onClose={() => touchField("waf_action_smart_parse")}
                      onChange={(e) => {
                        set_waf_action_smart_parse(parseInt(e.target.value));
                      }}
                    >
                      <MenuItem value={WafAction.DETECT} key={`SmartParseWafAction${WafAction.DETECT}`} sx={menuItemSx}>
                        Detect
                      </MenuItem>
                      <MenuItem value={WafAction.BLOCK} key={`SmartParseWafAction${WafAction.BLOCK}`} sx={menuItemSx}>
                        Block
                      </MenuItem>
                      <MenuItem value={WafAction.CHALLENGE} key={`SmartParseWafAction${WafAction.CHALLENGE}`} sx={menuItemSx}>
                        Challenge
                      </MenuItem>
                    </Select>
                    {showFieldErr("waf_action_smart_parse") && (
                      <FormHelperText error sx={helperTextSx}>{formValidation.errors.waf_action_smart_parse}</FormHelperText>
                    )}
                  </GreenSelectForm>
                  {waf_action_smart_parse === WafAction.CHALLENGE && (
                    <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("waf_challenge_kind_smart_parse"))}>
                      <InputLabel id="smart_parse_challenge_kind" shrink>Challenge kind</InputLabel>
                      <Select
                        labelId="smart_parse_challenge_kind"
                        label="Challenge kind"
                        IconComponent={ArrowdownIcon}
                        disabled={saving || !canConfigureWAF}
                        value={waf_challenge_kind_smart_parse ?? ""}
                        onClose={() => touchField("waf_challenge_kind_smart_parse")}
                        onChange={(e) => {
                          const v = e.target.value;
                          set_waf_challenge_kind_smart_parse(v === "" ? null : v);
                        }}
                        displayEmpty
                      >
                        <MenuItem value="" key="SmartParseChallengeKindDefault" sx={menuItemSx}>
                          Default (Managed)
                        </MenuItem>
                        <MenuItem value={ChallengeKind.INVISIBLE} key="SmartParseChallengeKindInvisible" sx={menuItemSx}>
                          Invisible
                        </MenuItem>
                        <MenuItem value={ChallengeKind.MANAGED} key="SmartParseChallengeKindManaged" sx={menuItemSx}>
                          Managed
                        </MenuItem>
                        <MenuItem value={ChallengeKind.ADAPTIVE} key="SmartParseChallengeKindAdaptive" sx={menuItemSx}>
                          Adaptive
                        </MenuItem>
                        <MenuItem value={ChallengeKind.INTERACTIVE} key="SmartParseChallengeKindInteractive" sx={menuItemSx}>
                          Interactive
                        </MenuItem>
                      </Select>
                      {showFieldErr("waf_challenge_kind_smart_parse") && (
                        <FormHelperText error sx={helperTextSx}>{formValidation.errors.waf_challenge_kind_smart_parse}</FormHelperText>
                      )}
                    </GreenSelectForm>
                  )}
                  <Stack
                    direction={"row"}
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary["alpha"],
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      {canConfigureWAF 
                        ? "You can update, review, or manage settings on this screen. Changes will be applied instantly after save!"
                        : "You don't have permission to configure WAF for this site. Please contact your administrator to request access."
                      }
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={3}>
                  <SaveButton 
                    onClick={handleSave} 
                    disabled={saving || !canSubmit}
                    title={!canConfigureWAF ? "You don't have permission to configure WAF for this site" : ""}
                  />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};

export default ConfigSmartParseWAFSettingsModal;
